import "./Sidebar.css";
import logo from "../../assets/Prople Logo.jpg";
import { ReactComponent as HomeIcon } from "../../assets/home.svg";
import { ReactComponent as HomeWhiteIcon } from "../../assets/Home-white.svg";
import { ReactComponent as RequestIcon } from "../../assets/Request.svg";
import { ReactComponent as RequestWhiteIcon } from "../../assets/RequestWhite.svg";
import { ReactComponent as SettingIcon } from "../../assets/setting.svg";
import { ReactComponent as ActivityIcon } from "../../assets/ActivityLog.svg";
import { ReactComponent as ActivityWhiteIcon } from "../../assets/WhiteActivityLog.svg";
import { ReactComponent as ClientCustomer } from "../../assets/ServiceFee.svg";
import { ReactComponent as ServiceFeeWhite } from "../../assets/ServiceFeeWhite.svg";
import { ReactComponent as ClientList } from "../../assets/ClientList.svg";
import { ReactComponent as ClientListWhiteIcon } from "../../assets/ClientListWhite.svg";
import SettingWhiteIcon from "../../assets/SettingWhite.png";
import { TeamOutlined, BarChartOutlined, FileTextOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { decryptObject } from "../GlobalFunction";
import { getMyPermissions } from "../../services/ApiService";

const readCachedPermissions = () => {
  try {
    const raw = sessionStorage.getItem("permissions");
    return raw ? JSON.parse(raw) : null;
  } catch (err) {
    return null;
  }
};

const Sidebar = ({ activeId }) => {
  const navigate = useNavigate();
  const userType = decryptObject(
    sessionStorage.getItem("sessioninfo")
  ).userType;

  // Seeded from cache so the menu renders instantly (no blank flash), then
  // refreshed on every mount (i.e. every page navigation, since Sidebar
  // remounts per page) so a role change made while this user is already
  // logged in shows up without needing a fresh login.
  const [permissions, setPermissions] = useState(readCachedPermissions);

  useEffect(() => {
    let cancelled = false;
    const refreshPermissions = async () => {
      try {
        const res = await getMyPermissions();
        if (!cancelled && res?.responseCode === 200) {
          sessionStorage.setItem(
            "permissions",
            JSON.stringify(res.data.permissions)
          );
          setPermissions(res.data.permissions);
        }
      } catch (err) {
        console.error("Failed to refresh permissions", err);
      }
    };
    refreshPermissions();
    return () => {
      cancelled = true;
    };
  }, []);

  // `module` maps an item to the Roles permission grid (constants.modules.MODULES
  // on the backend) -- an item shows only if the logged-in role has "show" on
  // that module. Items with no `module` (Home, Settings) aren't gated at all,
  // same as the backend doesn't gate them either.
  const menuConfig = [
    {
      id: 1,
      label: "Home",
      // CSP (userType 3) now shares the same richer Dashboard.js as
      // Admin/Manager -- confirmed with the user, 2026-10-29 -- rather
      // than the old sparse OPSDashboard.js ("/dashboard-e", left
      // registered but unreachable from here now, not deleted).
      // Dashboard.js already branches internally on userType === "1" to
      // scope everything to "my portfolio" for non-admins, so this is
      // just a routing change, not a new dashboard.
      path: userType === 4 ? "/dashboard-f" : "/dashboard",
      icon: <HomeIcon height={24} />,
      activeIcon: <HomeWhiteIcon height={24} />,
      visibleFor: [1, 2, 3, 4],
    },
    {
      id: 7,
      label: "Client List",
      path: "/clientList",
      icon: <ClientList height={24} />,
      activeIcon: <ClientListWhiteIcon height={24} />,
      visibleFor: [1, 4],
      module: "client_list",
    },
    {
      id: 10,
      label: "Customer List",
      path: "/clientCustomer",
      icon: <ClientList height={24} />,
      activeIcon: <ClientListWhiteIcon height={24} />,
      visibleFor: [1],
      module: "customer_list",
    },
    {
      id: 3,
      label: "Directory",
      path: "/masterDatabase",
      icon: <TeamOutlined style={{ fontSize: "1.4rem", color: "#787486" }} />,
      activeIcon: <TeamOutlined style={{ fontSize: "1.4rem" }} />,
      visibleFor: [1, 2, 3],
      module: "directory",
    },
    {
      id: 14,
      label: "Reports",
      path: "/reports",
      icon: <BarChartOutlined style={{ fontSize: "1.4rem", color: "#787486" }} />,
      activeIcon: <BarChartOutlined style={{ fontSize: "1.4rem" }} />,
      // Any user on the app can reach Reports, confirmed with the user,
      // 2026-10-28 -- same "visible to everyone" default as Home/Activity
      // Log, gated purely by the "reports" module permission once an
      // Admin grants it per role via the Roles page (same as every other
      // module-gated item here). Positioned right below Directory,
      // confirmed with the user, 2026-10-29.
      visibleFor: [1, 2, 3, 4],
      module: "reports",
    },
    {
      id: 15,
      label: "MCL Generator",
      path: "/mcl-generator",
      icon: <FileTextOutlined style={{ fontSize: "1.4rem", color: "#787486" }} />,
      activeIcon: <FileTextOutlined style={{ fontSize: "1.4rem" }} />,
      // Gated by the "mcl_generator" module permission, granted to Admin
      // and EOR Payroll by scripts/add_mcl_generator_module.py -- narrow
      // tool for the payroll team's Pag-IBIG/HDMF filing, confirmed with
      // the user, 2026-10-30.
      visibleFor: [1, 5],
      module: "mcl_generator",
    },
    // {
    //   id: 9,
    //   label: "All Records",
    //   path: "/allRecords",
    //   icon: <RequestIcon height={24} />,
    //   activeIcon: <RequestWhiteIcon height={24} />,
    //   visibleFor: [1],
    // },
    {
      id: 5,
      label: "Activity Log",
      path: "/activityLog",
      icon: <ActivityIcon height={24} />,
      activeIcon: <ActivityWhiteIcon height={24} />,
      visibleFor: [1, 2, 3, 4],
      module: "activity_log",
    },
    {
      id: 4,
      label: "Settings",
      path: "/settings",
      icon: <SettingIcon height={24} />,
      activeIcon: <img src={SettingWhiteIcon} height={24} alt="Setting" />,
      visibleFor: [1, 2, 3, 4],
    },
    {
      id: 6,
      label: "Fields",
      path: "/fields",
      icon: <SettingIcon height={24} />,
      activeIcon: <img src={SettingWhiteIcon} height={24} alt="Setting" />,
      visibleFor: [1],
      module: "fields",
    },
    {
      id: 8,
      label: "CountryList",
      path: "/countryList",
      icon: <SettingIcon height={24} />,
      activeIcon: <img src={SettingWhiteIcon} height={24} alt="Setting" />,
      visibleFor: [1],
      module: "country_list",
    },
    {
      id: 11,
      label: "Roles",
      path: "/roles",
      icon: <SettingIcon height={24} />,
      activeIcon: <img src={SettingWhiteIcon} height={24} alt="Setting" />,
      visibleFor: [1],
      module: "roles",
    },
    {
      id: 12,
      label: "Users",
      path: "/users",
      icon: <TeamOutlined style={{ fontSize: "1.4rem", color: "#787486" }} />,
      activeIcon: <TeamOutlined style={{ fontSize: "1.4rem" }} />,
      visibleFor: [1],
      module: "users",
    },
  ];

  const filteredMenu = menuConfig.filter((item) => {
    if (!item.module) return true; // Home/Settings -- not permission-gated
    if (permissions) {
      return (permissions[item.module] || []).includes("show");
    }
    // Fallback for a session cached before this change (no "permissions" key
    // yet) -- old behavior, until the user logs in again.
    return item.visibleFor.includes(userType) || userType === 1;
  });

  const handleNavigate = (path) => navigate(path);

  return (
    <div className="sidebar">
      <div
        className="logo"
        onClick={() =>
          handleNavigate(
            userType === 2 || userType === 3
              ? "/dashboard"
              : userType === 4
              ? "/dashboard-f"
              : "/dashboard-admin"
          )
        }
      >
        <img src={logo} alt="logo" />
      </div>

      <div className="menu">
        {filteredMenu.map((item) => (
          <div
            key={item.id}
            className={`menu-item ${activeId === item.id ? "activeItem" : ""}`}
            onClick={() => handleNavigate(item.path)}
          >
            {activeId === item.id ? item.activeIcon : item.icon}
            <span className="menu-title">{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Sidebar;
